import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Copy, MoreVertical, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { getErrorMessage } from '@/api/errors'
import { extractValidationMessage } from '@/lib/validationErrors'
import { useAuth, useDuplicateAgent } from '@/hooks'
import { Permissions } from '@/lib/permissions'
import type { Agent } from '@/types'
import { DeleteAgentDialog } from './DeleteAgentDialog'

interface AgentActionsMenuProps {
  projectId: string
  agent: Agent
}

export function AgentActionsMenu({ projectId, agent }: AgentActionsMenuProps) {
  const navigate = useNavigate()
  const { hasPermission } = useAuth(projectId)
  const [deleting, setDeleting] = useState(false)
  const duplicateAgent = useDuplicateAgent(projectId)

  const canDelete = !agent.isReadOnly && hasPermission(Permissions.Project.Agent.Delete, projectId)
  const canDuplicate = hasPermission(Permissions.Project.Agent.Create, projectId)

  if (!canDelete && !canDuplicate) {
    return null
  }

  const handleDuplicate = async () => {
    try {
      const result = await duplicateAgent.mutateAsync(agent.id)
      toast.success(`Duplicated "${agent.displayName}"`)
      navigate(`/projects/${projectId}/workflows/agents/${(result as { agentId: string }).agentId}/config`)
    } catch (error) {
      toast.error(extractValidationMessage(error) ?? getErrorMessage(error) ?? 'Failed to duplicate agent')
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            aria-label={`Actions for ${agent.displayName}`}
            data-testid={`agent-actions-${agent.name}`}
            onClick={e => e.stopPropagation()}
          >
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={e => e.stopPropagation()}>
          {canDuplicate && (
            <DropdownMenuItem
              data-testid={`agent-duplicate-${agent.name}`}
              onSelect={e => {
                e.preventDefault()
                void handleDuplicate()
              }}
              disabled={duplicateAgent.isPending}
            >
              <Copy className="h-4 w-4" />
              {duplicateAgent.isPending ? 'Duplicating...' : 'Duplicate'}
            </DropdownMenuItem>
          )}
          {canDelete && (
            <DropdownMenuItem className="text-destructive focus:text-destructive" onSelect={() => setDeleting(true)}>
              <Trash2 className="h-4 w-4" />
              Delete
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {canDelete && <DeleteAgentDialog projectId={projectId} agent={agent} open={deleting} onOpenChange={setDeleting} />}
    </>
  )
}
